import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, GripVertical, Users2, Handshake, Trophy, Gift, Save } from "lucide-react";

const EMPTY_TIER = () => ({ name: "", min: 0, benefits: "" });

function TierEditor({ label, icon: Icon, tiers, setTiers, roleKey }) {
  const upd = (i, k, v) => setTiers(tiers.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const add = () => setTiers([...tiers, EMPTY_TIER()]);
  const rm = (i) => setTiers(tiers.filter((_, idx) => idx !== i));
  return (
    <div className="velo-card p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
            <Icon className="w-4 h-4 text-pink-400" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-white font-medium">{label}</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-widest">Ordered by minimum points</div>
          </div>
        </div>
        <Button data-testid={`btn-add-tier-${roleKey}`} size="sm" variant="outline" onClick={add} className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5">
          <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Add tier
        </Button>
      </div>
      <div className="space-y-3">
        {tiers.map((t, i) => (
          <div key={i} data-testid={`tier-row-${roleKey}-${i}`} className="velo-surface rounded-lg p-4 grid grid-cols-1 sm:grid-cols-[24px_1fr_120px_1fr_36px] gap-3 items-start">
            <div className="text-slate-500 mt-2 hidden sm:block"><GripVertical className="w-4 h-4" strokeWidth={1.5} /></div>
            <div>
              <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Tier name</Label>
              <Input data-testid={`input-tier-name-${roleKey}-${i}`} value={t.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Gold" className="velo-input mt-1.5 h-9" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Points min</Label>
              <Input data-testid={`input-tier-min-${roleKey}-${i}`} type="number" value={t.min} onChange={(e) => upd(i, "min", Number(e.target.value))} className="velo-input mt-1.5 h-9 font-mono" />
            </div>
            <div className="sm:col-span-1">
              <Label className="text-[10px] text-slate-500 uppercase tracking-widest">Benefits / perks</Label>
              <Input data-testid={`input-tier-benefits-${roleKey}-${i}`} value={t.benefits || ""} onChange={(e) => upd(i, "benefits", e.target.value)} placeholder="e.g. 10% bonus points" className="velo-input mt-1.5 h-9" />
            </div>
            <Button data-testid={`btn-remove-tier-${roleKey}-${i}`} size="sm" variant="ghost" onClick={() => rm(i)} disabled={tiers.length <= 1} className="text-slate-500 hover:text-red-400 hover:bg-red-500/10 h-9 w-9 p-0 mt-6 justify-self-end">
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SettingsTab() {
  const [customer, setCustomer] = useState([]);
  const [affiliate, setAffiliate] = useState([]);
  const [upgradeNote, setUpgradeNote] = useState("");
  const [limits, setLimits] = useState({ customer: 5, affiliate: 25 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/settings");
    setCustomer((data.tier_slabs?.customer || []).map((t) => ({ ...t })));
    setAffiliate((data.tier_slabs?.affiliate || []).map((t) => ({ ...t })));
    setUpgradeNote(data.upgrade_note || "");
    setLimits(data.max_active_referrals || { customer: 5, affiliate: 25 });
  }, []);
  useEffect(() => { load(); }, [load]);

  const sortByMin = (list) => [...list].sort((a, b) => Number(a.min) - Number(b.min));

  const saveTiers = async () => {
    // Basic validation
    for (const list of [customer, affiliate]) {
      if (list.some((t) => !t.name?.trim())) { toast.error("Every tier needs a name"); return; }
      const mins = list.map((t) => Number(t.min));
      if (mins.some((m) => Number.isNaN(m) || m < 0)) { toast.error("Points min must be zero or more"); return; }
      if (new Set(mins).size !== mins.length) { toast.error("Two tiers share the same points min — please differentiate"); return; }
    }
    setBusy(true);
    try {
      await api.put("/admin/settings/tiers", {
        customer: sortByMin(customer),
        affiliate: sortByMin(affiliate),
        upgrade_note: upgradeNote,
      });
      toast.success("Tier configuration saved — reflecting live");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save tiers");
    } finally { setBusy(false); }
  };

  const saveLimits = async () => {
    const c = Number(limits.customer), a = Number(limits.affiliate);
    if (!(c >= 1 && a >= 1)) { toast.error("Limits must be at least 1"); return; }
    setBusy(true);
    try {
      await api.put("/admin/settings/referral-limits", { customer: c, affiliate: a });
      toast.success(`Referral limits updated: Customer ${c} · Affiliate ${a}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save limits");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-white text-xl font-medium">Programme configuration</h2>
        <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">Tiers · referral caps · perks</p>
      </div>

      {/* Referral limits */}
      <div className="velo-card p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Handshake className="w-4 h-4 text-amber-400" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-white font-medium">Active referral limits</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-widest">Max pending referrals allowed at a time</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-widest">Customer</Label>
            <Input data-testid="input-limit-customer" type="number" min="1" max="500" value={limits.customer} onChange={(e) => setLimits({ ...limits, customer: Number(e.target.value) })} className="velo-input mt-2 h-11 font-mono" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-widest">Affiliate</Label>
            <Input data-testid="input-limit-affiliate" type="number" min="1" max="500" value={limits.affiliate} onChange={(e) => setLimits({ ...limits, affiliate: Number(e.target.value) })} className="velo-input mt-2 h-11 font-mono" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="btn-save-limits" onClick={saveLimits} disabled={busy} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">
            <Save className="w-4 h-4 mr-2" strokeWidth={1.75} /> Save limits
          </Button>
        </div>
      </div>

      {/* Tier config */}
      <TierEditor label="Customer tiers" icon={Users2} tiers={customer} setTiers={setCustomer} roleKey="customer" />
      <TierEditor label="Affiliate tiers" icon={Trophy} tiers={affiliate} setTiers={setAffiliate} roleKey="affiliate" />

      <div className="velo-card p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <Gift className="w-4 h-4 text-emerald-400" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-white font-medium">Tier upgrade note</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-widest">Shown to members below their tier ladder</div>
          </div>
        </div>
        <Textarea data-testid="input-upgrade-note" value={upgradeNote} onChange={(e) => setUpgradeNote(e.target.value)} className="velo-input min-h-[70px]" placeholder="Each tier upgrade comes with a special gift from Veloskin." />
      </div>

      <div className="flex justify-end">
        <Button data-testid="btn-save-tiers" onClick={saveTiers} disabled={busy} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black h-11 px-6">
          <Save className="w-4 h-4 mr-2" strokeWidth={1.75} /> Save tiers &amp; benefits
        </Button>
      </div>
    </div>
  );
}
